/**
 * FieldGrpPropAddUpdate.tsx
 * Field-group property editor (name/label + match rule tab).
 * Redesigned — uses shared GenWizard light theme.
 *
 * LAZY-LOAD FIX
 * -------------
 * The Match Rule tab for an ENTITY renders <MatchRuleOnEntity> driven by the
 * entity's matchRules array. Pre-lazy-load that array arrived on initialData
 * (the full refinedDM entry). Under lazy loading initialData is a SHELL with no
 * matchRules, so the tab went blank. DataModelTreeLazy now fetches the entity's
 * match rules (getMatchRules) and passes them in via the `matchRules` prop; we
 * prefer that prop and fall back to initialData.matchRules for the non-lazy path.
 */

import React, { Component, ChangeEvent } from 'react';

import { cls, injectTheme } from './theme/theme';
import MatchRule from './MatchRule';
import MatchRuleOnEntity from './dm/MatchRuleOnEntity';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import type { MatchRule as MatchRuleType } from './mdmAPI';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FieldGrpPropAddUpdateProps {
  tool: string;
  handleSave: (data: any) => void;
  initialData: any;
  srcSystems: any;
  // NEW (lazy): entity match rules fetched by DataModelTreeLazy via getMatchRules.
  // Optional so the non-lazy path (full initialData) keeps working unchanged.
  matchRules?: MatchRuleType[];
  matchRulesLoading?: boolean;
}

interface FieldGrpPropAddUpdateState {
  fieldData: any;
  tabKey: string;
  triggerUpdate: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────
class FieldGrpPropAddUpdate extends Component<FieldGrpPropAddUpdateProps, FieldGrpPropAddUpdateState> {
  constructor(props: FieldGrpPropAddUpdateProps) {
    super(props);
    this.state = {
      fieldData:     { ...props.initialData },
      tabKey:        'home',
      triggerUpdate: false,
    };
  }

  componentDidMount() {
    injectTheme();
  }

  componentDidUpdate(prev: FieldGrpPropAddUpdateProps) {
    if (prev.initialData !== this.props.initialData) {
      this.setState({ fieldData: { ...this.props.initialData } });
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  handleTabSelect = (key: string) => this.setState({ tabKey: key });

  handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { type, name, value, checked } = e.target;
    this.setState(prev => ({
      fieldData: { ...prev.fieldData, [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value },
    }));
  };

  handleSaveSystemOrderChanges = (data: any) => {
    if (!data) return;
    const fData = { ...this.state.fieldData, SYSTEM_ORDER: data };
    this.setState({ fieldData: fData });
  };

  setTriggerUpdate = () => this.setState({ triggerUpdate: true });

  handleUpdateMtchConfigChanges = (cfg: any) => {
    const fData = {
      ...this.state.fieldData,
      matchFieldList:               cfg.matchFieldList,
      matchFieldListExactOrAllNull: cfg.matchFieldListExactOrAllNull,
      matchFieldListExactOrNull:    cfg.matchFieldListExactOrNull,
    };
    this.setState({ triggerUpdate: false, fieldData: fData });
    this.props.handleSave(fData);
  };

  handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    this.props.handleSave(this.state.fieldData);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  render() {
    const { fieldData, tabKey } = this.state;
    const { matchRules, matchRulesLoading } = this.props;
    const fldList = fieldData.field?.filter((f: any) => f.eClass === 'Field') || [];
    const mtchConfig = {
      matchFieldList:               fieldData.matchFieldList,
      matchFieldListExactOrAllNull: fieldData.matchFieldListExactOrAllNull,
      matchFieldListExactOrNull:    fieldData.matchFieldListExactOrNull,
    };

    // Prefer the lazily-fetched match rules; fall back to the rules carried on
    // the full entry (non-lazy path). Under lazy loading fieldData is a shell,
    // so fieldData.matchRules is undefined and the prop supplies the data.
    const entityMatchRules = matchRules ?? fieldData.matchRules ?? [];

    const tabs = [
      { key: 'home',      label: 'Properties', icon: '🏷️' },
      { key: 'matchRule', label: 'Match Rule',  icon: '📊' },
    ];

    return (
      <div className="gw-root">

        {/* ── Header ── */}
        <div className="gw-field-hdr">
          <div>
            <div className="gw-field-hdr-name">
              {fieldData.label || fieldData.name || 'Field Group'}
            </div>
            <div className="gw-field-hdr-meta">
              <span className="gw-badge gw-badge-brand">Field Group</span>
              <span className="gw-badge gw-badge-muted">{fldList.length} fields</span>
            </div>
          </div>
          <button
            className="gw-btn gw-btn-primary gw-btn-sm"
            onClick={() => { this.setTriggerUpdate(); this.handleSubmit(); }}
          >
            Save
          </button>
        </div>

        {/* ── Tabs ── */}
        <div className="gw-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={cls('gw-tab', tabKey === tab.key && 'active')}
              onClick={() => this.handleTabSelect(tab.key)}
            >
              <span className="gw-tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        <div style={{ padding: '20px 18px' }}>

          {tabKey === 'home' && (
            <div className="gw-form-grid">
              <div className="gw-form-group">
                <label className="gw-label">Name</label>
                <input className="gw-input" name="name"
                  value={fieldData.name || ''} onChange={this.handleChange} />
              </div>
              <div className="gw-form-group">
                <label className="gw-label">Display Label</label>
                <input className="gw-input" name="label"
                  value={fieldData.label || ''} onChange={this.handleChange} />
              </div>
            </div>
          )}

          {tabKey === 'matchRule' && (
            fieldData.entityId
              ? (
                  // Entity-level match rules. Show a spinner while the lazy fetch
                  // is in flight and nothing has arrived yet, so the tab never
                  // looks "blank" during loading.
                  matchRulesLoading && entityMatchRules.length === 0
                    ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                        padding: '24px 4px', color: '#445566', fontSize: 13 }}>
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading match rules…
                      </div>
                    )
                    : (
                      <MatchRuleOnEntity
                        dedupsRules={entityMatchRules}
                        fieldOptionsModel={fieldData}
                      />
                    )
                )
              : <MatchRule
                  fields={fldList}
                  mtchConfig={mtchConfig}
                  triggerUpdate={this.state.triggerUpdate}
                  handleUpdateMtchConfigChanges={this.handleUpdateMtchConfigChanges}
                />
          )}
        </div>
      </div>
    );
  }
}

export default FieldGrpPropAddUpdate;